/**
 * FileList Component
 *
 * Displays a list of files with download and delete actions.
 */

import { type Component, createSignal, For, Show } from 'solid-js';
import { downloadFile, deleteFile as deleteFileApi, type FileInfo } from '../../services/api';

interface FileListProps {
  files: FileInfo[];
  onDelete?: (fileId: string) => void;
  onRefresh?: () => void;
  loading?: boolean;
  emptyMessage?: string;
}

export const FileList: Component<FileListProps> = (props) => {
  const [deletingId, setDeletingId] = createSignal<string | null>(null);
  const [downloadingId, setDownloadingId] = createSignal<string | null>(null);

  const handleDownload = async (file: FileInfo) => {
    setDownloadingId(file.id);
    try {
      await downloadFile(file.id);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (file: FileInfo) => {
    if (!confirm(`Delete "${file.displayName || file.name}"?`)) return;

    setDeletingId(file.id);
    try {
      await deleteFileApi(file.id);
      props.onDelete?.(file.id);
      props.onRefresh?.();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z';
    if (mimeType.includes('pdf')) return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
    if (mimeType.startsWith('text/') || mimeType.includes('json') || mimeType.includes('javascript') || mimeType.includes('typescript')) return 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4';
    if (mimeType.includes('csv') || mimeType.includes('spreadsheet')) return 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z';
    return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
  };

  const getFileTypeLabel = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('json')) return 'JSON';
    if (mimeType.includes('javascript') || mimeType.includes('typescript')) return 'Code';
    if (mimeType.includes('markdown')) return 'Markdown';
    if (mimeType.includes('csv')) return 'CSV';
    if (mimeType.includes('yaml')) return 'YAML';
    if (mimeType.startsWith('text/')) return 'Text';
    return 'File';
  };

  return (
    <div class="space-y-2">
      {/* Loading State */}
      <Show when={props.loading}>
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500" />
        </div>
      </Show>

      {/* Empty State */}
      <Show when={!props.loading && props.files.length === 0}>
        <div class="text-center py-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p>{props.emptyMessage || 'No files uploaded yet'}</p>
        </div>
      </Show>

      {/* File List */}
      <Show when={!props.loading && props.files.length > 0}>
        <div class="divide-y divide-gray-800 rounded-lg border border-gray-800 overflow-hidden">
          <For each={props.files}>
            {(file) => (
              <div class="flex items-center gap-4 p-4 bg-gray-900/50 hover:bg-gray-800/50 transition-colors">
                {/* File Icon */}
                <div class="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={getFileIcon(file.mimeType)} />
                  </svg>
                </div>

                {/* File Info */}
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-200 truncate">
                    {file.displayName || file.name}
                  </p>
                  <div class="flex items-center gap-3 text-xs text-gray-500">
                    <span class="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">
                      {getFileTypeLabel(file.mimeType)}
                    </span>
                    <span>{formatFileSize(file.sizeBytes)}</span>
                    <span>{formatDate(file.createdAt)}</span>
                  </div>
                  <Show when={file.description}>
                    <p class="text-xs text-gray-500 mt-1 truncate">{file.description}</p>
                  </Show>
                </div>

                {/* Actions */}
                <div class="flex items-center gap-2 flex-shrink-0">
                  {/* Download Button */}
                  <button
                    onClick={() => handleDownload(file)}
                    disabled={downloadingId() === file.id}
                    class="p-2 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:opacity-50 transition-colors"
                    title="Download"
                  >
                    <Show
                      when={downloadingId() !== file.id}
                      fallback={
                        <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      }
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </Show>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDelete(file)}
                    disabled={deletingId() === file.id}
                    class="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                    title="Delete"
                  >
                    <Show
                      when={deletingId() !== file.id}
                      fallback={
                        <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      }
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Show>
                  </button>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};
